import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Client } from 'core/models';
import {
  BreadcrumbService,
  UtilsService,
  ProfileService,
  PopUpService,
  GamesService,
  NotificationsService
} from 'core/services';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { MarsGroupService } from 'app/mars/services/mars-groups.service';
import { MarsGroup } from 'app/mars/models/MarsGroup';

@Component({
  selector: 'lms-mars-group',
  templateUrl: './mars-group.component.html',
  styleUrls: ['./mars-group.component.scss']
})
export class MarsGroupComponent implements OnInit, OnDestroy, AfterViewInit {
  public group: MarsGroup;
  public client: Client;
  public now = Date.now();
  public supervisorUrl = '';
  public tabsManager: any;
  public idButton = 'idButtonDataGroup';
  public filters: any;

  @ViewChild('tabGroup', { static: false }) public tabGroup: MatTabGroup;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public location: Location,
    public translate: TranslateService,
    public titleService: Title,
    public breadcrumb: BreadcrumbService,
    public profileService: ProfileService,
    public popupService: PopUpService,
    public gamesService: GamesService,
    public notificationsService: NotificationsService,
    public groupsService: MarsGroupService
  ) {}

  ngOnInit() {
    this.route.data.subscribe((data) => {
      this.group = data['group'];
      this.client = data['client'];
      this.titleService.setTitle(
        this.group
          ? `${this.group.name} - `
          : `${this.translate.instant('mars.label')} - ${this.translate.instant('pageTitle.gamelearn')}`
      );
      if (this.group) {
        this.filters = { groupId: this.group.id };
      }
    });
  }

  public ngAfterViewInit(): void {
    this.tabsManager = UtilsService.createTabsManager(this.route, this.tabGroup);
  }

  public ngOnDestroy() {
    this.tabsManager.unsubscribe();
  }

  public goBack(): void {
    this.location.back();
  }

  public setTab(selectedTab: MatTabChangeEvent) {
    this.tabsManager.setTab(selectedTab);
  }

  onGroupDataSaved(group: MarsGroup) {
    if (!this.group) {
      this.router.navigate(['/mars', group.id], {
        replaceUrl: true,
        fragment: 'enrollment'
      });
    } else {
      this.group = group;
    }
  }

  onUpdate(group: MarsGroup) {
    this.group = group;
  }

  onDelete() {
    this.goBack();
  }

  onUpdateEnrollment(updatedGroup) {
    this.group = updatedGroup;
  }

  /**
   *  Reset the game progress to the initial point of the given license.
   */
  resetGameSupervisor() {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('games.confirmResetGame'),
        confirmButtonText: this.translate.instant('actions.accept')
      })
      .then(() => this.gamesService.resetGame(this.group.gameId, this.getSupervisorEmail()));
  }

  deleteGroup() {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('groups.confirmDelete', {
          group: this.group.name
        }),
        text: this.translate.instant('groups.deleteMessage')
      })
      .then(() => this.groupsService.delete(this.group.id))
      .then(() => this.onDelete())
      .catch((error) => {
        if (error !== 'cancel') {
          const errorLabel = `error.${error.errorCode}`;
          const translatedErrorMessage = this.translate.instant(errorLabel);

          this.popupService.showError({
            title: this.translate.instant('groups.deleteError'),
            text: translatedErrorMessage === errorLabel ? error.serverMessage : translatedErrorMessage
          });
        }
      });
  }

  resendBlueMail() {
    this.notificationsService
      .sendBonusGroupCreatedNotification(this.group)
      .then(() => {
        this.popupService.showSuccess({
          title: this.translate.instant('groups.blueMailSent')
        });
      })
      .catch((err) => {
        this.popupService.showError({
          title: this.translate.instant('groups.blueMailError'),
          text: err
        });
      });
  }

  /**
   *  Build the supervisor email as license identification.
   *  @return  {string}  The supervisor email.
   */
  getSupervisorEmail() {
    return `group_${this.group.id}@supervisor.gamelearn.net`;
  }

  supervisorUrlOpen() {
    if (this.supervisorUrl) {
      window.open(this.supervisorUrl, '_blank');
    }
  }
}
